import React, { useMemo, useState } from 'react';
import { Card, Button, HTMLSelect, InputGroup, Tooltip, Icon, SegmentedControl, Switch } from '@blueprintjs/core';
import { useLoaderData, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconNames } from '@blueprintjs/icons';
import { Parameter, ParametersCard } from './ParametersCard';
// import HeatMapCard from './HeatmapCard';
import SimulationGraph, { getLegendItems, getSignalSeries } from './SimulationGraph';
import { getValueRange, isIdentityTransform, sanitizeTransforms, SignalTransform, ValueRange } from './signalTransform';
import SimulationGraph3D, { Axis3D, AXES_3D, getDefaultAxes3D } from './SimulationGraph3D';
import SimulationTable from './SimulationTable';
import SimulationPlayer, { getDefaultPlayerAxes, PlayerAxis, PLAYER_AXES } from './SimulationPlayer';
import { AxisSelection, getAxisOptions, getAxisTraces, resolveSelection } from './signalAxes';
import { AxisSelector } from './AxisSelector';
import { msToSeconds } from '../utils/time';
import { ExecutionSummary, getExecutions, getLatestExecutionId, getSimulationInputs, getSimulationResultsById, InputsDocument, InputSeries, saveSimulationInputs, SimulationExecution, TimeSeriesInput } from '../api/cppmodelApi';
import { InputEditor } from './InputEditor';
import { areSeriesEqual, areSeriesListsEqual } from './inputEditing';
import { RunReportDrawer, RunReportTab } from './RunReportDrawer';
import { useResizablePane } from './useResizablePane';
import { FocusRequest, getCoverageInterval, getFocusRange, GraphHighlight, parseRequirements, parseVerification, requirementHighlightId, ruleHighlightId } from './verification';

// interface Parameter {
//     name: string;
//     value: string;
// };

export interface ApiGraphData extends SimulationExecution {
    executionTime: string;
};

type LocalRunStatus = 'idle' | 'running' | 'completed' | 'failed';
type GraphMode = '2d' | '3d' | 'player' | 'table';

const DURATION_PARAMETER = 'CppModel.SimulationTime';
const STEP_SIZE_PARAMETER = 'CppModel.SimulationStepSize';
// The CppModel.* parameters a run reads; the others (like
// CppModel.BinaryFingerprint) describe a finished run and are not posted.
const RESERVED_INPUT_PARAMETERS = [DURATION_PARAMETER, STEP_SIZE_PARAMETER];
const LOCAL_SIMULATION_WS_URL = 'ws://localhost:9090';
// How often to check the workspace for a newly submitted execution (ms).
const EXECUTION_POLL_INTERVAL = 5000;
const STEP_RESULT_LABEL = 'CppModel.StepResult';
const VIEW_STATE_STORAGE_KEY = 'cm-results-view';

// What the user chose to look at, remembered per simulation.
interface ViewState {
    graphMode: GraphMode;
    traceVisibility: Record<string, boolean>;
    selected3D: AxisSelection<Axis3D>;
    selectedPlayer: AxisSelection<PlayerAxis>;
    editingInput: string | null;
    transforms: Record<string, SignalTransform>;
    // Load new executions as they are submitted. Off by default, so the graph
    // never changes while someone is looking at it unless they ask for it.
    autoRefresh: boolean;
}

const DEFAULT_VIEW_STATE: ViewState = {
    graphMode: '2d',
    traceVisibility: {},
    selected3D: [],
    selectedPlayer: [],
    editingInput: null,
    transforms: {},
    autoRefresh: false,
};

const getViewStateKey = (simulationId?: string) => `${VIEW_STATE_STORAGE_KEY}:${simulationId ?? ''}`;

const loadViewState = (simulationId?: string): ViewState => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(getViewStateKey(simulationId)) ?? 'null');

        if (!stored || typeof stored !== 'object') {
            return DEFAULT_VIEW_STATE;
        }

        return {
            graphMode: ['2d', '3d', 'player', 'table'].includes(stored.graphMode) ? stored.graphMode : DEFAULT_VIEW_STATE.graphMode,
            traceVisibility: stored.traceVisibility && typeof stored.traceVisibility === 'object' ? stored.traceVisibility : {},
            // Older versions stored per-axis lists; those fall back to the defaults.
            selected3D: Array.isArray(stored.selected3D) ? stored.selected3D : [],
            selectedPlayer: Array.isArray(stored.selectedPlayer) ? stored.selectedPlayer : [],
            editingInput: typeof stored.editingInput === 'string' ? stored.editingInput : null,
            transforms: sanitizeTransforms(stored.transforms),
            autoRefresh: stored.autoRefresh === true,
        };
    } catch {
        return DEFAULT_VIEW_STATE;
    }
};

const storeViewState = (simulationId: string | undefined, viewState: ViewState) => {
    try {
        window.localStorage.setItem(getViewStateKey(simulationId), JSON.stringify(viewState));
    } catch {
        // Remembering the view is only a convenience.
    }
};

interface LocalRunRequest {
    type: 'runSimulation';
    parameters: Record<string, number>;
    inputs: Record<string, TimeSeriesInput>;
}

interface LocalRunResultsMessage {
    type: 'results';
    requestType: 'runSimulation';
    results: ApiGraphData;
}

interface LocalRunErrorMessage {
    type: 'error';
    code?: string;
    message?: string;
    requestType?: string;
}

type LocalRunMessage = LocalRunResultsMessage | LocalRunErrorMessage;

export interface TestStatus {
    passed: boolean;
    failedSteps: number;
    totalSteps: number;
    firstFailureTime: number | null;
}

// Derive the overall test verdict from the CppModel.StepResult output: the test
// passes only when every step result is truthy. Returns null when the
// simulation does not produce a step result output.
export const getTestStatus = (results: ApiGraphData['results']): TestStatus | null => {
    const stepResults = results?.filter((result) => result.label.includes(STEP_RESULT_LABEL)) ?? [];

    if (stepResults.length === 0) {
        return null;
    }

    let failedSteps = 0;
    let totalSteps = 0;
    let firstFailureTime: number | null = null;

    stepResults.forEach((result) => {
        result.y.forEach((value, index) => {
            totalSteps += 1;

            if (!value) {
                failedSteps += 1;

                const time = result.x[index];
                if (firstFailureTime === null || time < firstFailureTime) {
                    firstFailureTime = time;
                }
            }
        });
    });

    return { passed: failedSteps === 0, failedSteps, totalSteps, firstFailureTime };
};

// Simulations may return results without inputs (or parameters/results), as
// missing or null fields. Normalize once so the rest of the page can rely on
// arrays and objects being present. Parameters keep only numbers: SDK 0.11.0
// also stored verification outcomes there.
export const normalizeGraphData = (data: Partial<ApiGraphData> | null | undefined): ApiGraphData => {
    const { inputs, results, parameters, executionTime, verification, requirements } = data ?? {};
    const parameterEntries = parameters && typeof parameters === 'object' ? Object.entries(parameters) : [];

    return {
        inputs: Array.isArray(inputs) ? inputs : [],
        results: Array.isArray(results) ? results : [],
        parameters: Object.fromEntries(parameterEntries.filter(([, value]) => typeof value === 'number')),
        executionTime: executionTime ?? '',
        verification: parseVerification(verification, parameters),
        requirements: parseRequirements(requirements),
    };
};

// Rules with violations are shown on the graph until the user hides them.
const getDefaultShownOnGraph = (data: ApiGraphData) => new Set(
    Object.entries(data.verification ?? {})
        .filter(([, outcome]) => outcome.violations.length > 0)
        .map(([name]) => ruleHighlightId(name))
);

// End of the run in seconds: the configured duration, or else the last time
// any signal reports.
const getRunEnd = (data: ApiGraphData): number => {
    const duration = data.parameters[DURATION_PARAMETER];

    if (typeof duration === 'number' && duration > 0) {
        return duration;
    }

    return msToSeconds(Math.max(0, ...[...data.inputs, ...data.results].flatMap((series) => series.x)));
};

const isEditableParameter = (name: string): boolean => {
    return !name.startsWith('CppModel.') || RESERVED_INPUT_PARAMETERS.includes(name);
};

// What the next execution will read: the inputs and parameters of the latest
// execution (which include the code's fallbacks), overridden by the document
// pending for the next execution, if any.
const buildNextRunValues = (execution: ApiGraphData, pending: InputsDocument | null) => {
    const parameterValues: Record<string, string> = {};
    const parameters = { ...execution.parameters, ...pending?.parameters };

    Object.entries(parameters).forEach(([name, value]) => {
        if (isEditableParameter(name)) {
            parameterValues[name] = value.toString();
        }
    });

    const inputs = execution.inputs.map((input) => (
        pending?.inputs.find((pendingInput) => pendingInput.label === input.label) ?? input
    ));
    pending?.inputs.forEach((pendingInput) => {
        if (!inputs.some((input) => input.label === pendingInput.label)) {
            inputs.push(pendingInput);
        }
    });

    return { parameterValues, inputs: inputs.map(({ label, x, y }) => ({ label, x, y })) };
};

const areParameterValuesEqual = (
    firstValues: Record<string, string>,
    secondValues: Record<string, string>
): boolean => {
    const allKeys = new Set([
        ...Object.keys(firstValues),
        ...Object.keys(secondValues),
    ]);

    for (const key of Array.from(allKeys)) {
        if ((firstValues[key] ?? '') !== (secondValues[key] ?? '')) {
            return false;
        }
    }

    return true;
};

const buildLocalRunRequest = (
    parameterValues: Record<string, string>,
    inputValues: InputSeries[]
): LocalRunRequest => {
    const parameters: Record<string, number> = {};
    const inputs: Record<string, TimeSeriesInput> = {};

    Object.entries(parameterValues).forEach(([name, value]) => {
        parameters[name] = Number(value);
    });

    inputValues.forEach((input) => {
        inputs[input.label] = {
            x: input.x,
            y: input.y,
        };
    });

    return {
        type: 'runSimulation',
        parameters,
        inputs,
    };
};

// Every input and parameter goes into the document: a name left out would make
// the next execution use the code's fallback.
const buildInputsDocument = (parameterValues: Record<string, string>, inputValues: InputSeries[]): InputsDocument => {
    const parameters: Record<string, number> = {};

    Object.entries(parameterValues).forEach(([name, value]) => {
        parameters[name] = Number(value);
    });

    return { inputs: inputValues, parameters };
};

const runLocalSimulationOnce = (request: LocalRunRequest): Promise<ApiGraphData> => {
    return new Promise((resolve, reject) => {
        const socket = new WebSocket(LOCAL_SIMULATION_WS_URL);
        let hasSettled = false;

        const settle = (callback: () => void) => {
            if (hasSettled) {
                return;
            }

            hasSettled = true;
            callback();
            socket.close();
        };

        socket.onopen = () => {
            socket.send(JSON.stringify(request));
        };

        socket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data) as LocalRunMessage;

                if (message.type === 'results' && message.requestType === 'runSimulation') {
                    settle(() => resolve(message.results));
                    return;
                }

                if (message.type === 'error') {
                    settle(() => reject(new Error(message.message ?? message.code ?? 'Local simulation failed.')));
                    return;
                }

                settle(() => reject(new Error('Unexpected local simulation response.')));
            } catch (error) {
                settle(() => reject(error));
            }
        };

        socket.onerror = () => {
            settle(() => reject(new Error('Could not connect to the local simulation WebSocket.')));
        };

        socket.onclose = () => {
            if (!hasSettled) {
                settle(() => reject(new Error('The local simulation WebSocket closed before returning results.')));
            }
        };
    });
};

const runLocalSimulation = async (request: LocalRunRequest): Promise<ApiGraphData> => {
    try {
        return await runLocalSimulationOnce(request);
    } catch (firstError) {
        await new Promise((resolve) => window.setTimeout(resolve, 500));

        try {
            return await runLocalSimulationOnce(request);
        } catch {
            throw firstError;
        }
    }
};

const SimulationResultsPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { simulationId, executionId } = useParams();
    // Only in-app navigation passes the name; a shared link has just the id.
    const simulationName = location.state?.simulationName ?? simulationId;
    const loaderData = useLoaderData() as Partial<ApiGraphData> | null;
    const rawData = useMemo(() => normalizeGraphData(loaderData), [loaderData]);
    const [graphData, setGraphData] = useState<ApiGraphData>(rawData);
    const [executionTime, setExecutionTime] = useState(rawData.executionTime);

    const { paneWidth, isResizingPane, bodyRef: resultsBodyRef, splitterProps } = useResizablePane();
    const [initialViewState] = useState(() => loadViewState(simulationId));
    const [traceVisibility, setTraceVisibility] = useState(initialViewState.traceVisibility);
    const [graphMode, setGraphMode] = useState<GraphMode>(initialViewState.graphMode);
    const [transforms, setTransforms] = useState(initialViewState.transforms);
    const [autoRefresh, setAutoRefresh] = useState(initialViewState.autoRefresh);
    const [selected3D, setSelected3D] = useState(initialViewState.selected3D);
    const [selectedPlayer, setSelectedPlayer] = useState(initialViewState.selectedPlayer);
    // Values for the next run as edited, as saved for the next execution, and
    // as sent with the last local run.
    const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
    const [originalParameterValues, setOriginalParameterValues] = useState<Record<string, string>>({});
    const [lastRunParameterValues, setLastRunParameterValues] = useState<Record<string, string>>({});
    const [inputValues, setInputValues] = useState<InputSeries[]>(rawData.inputs);
    const [originalInputValues, setOriginalInputValues] = useState<InputSeries[]>(rawData.inputs);
    const [lastRunInputValues, setLastRunInputValues] = useState<InputSeries[]>(rawData.inputs);
    const [editingInput, setEditingInputState] = useState(initialViewState.editingInput);
    // The signal shifted and scaled by dragging on the 2D graph. Dragging
    // either adjusts a signal or edits an input's points, never both.
    const [adjustingSignal, setAdjustingSignal] = useState<string | null>(null);
    const [hasPendingInputs, setHasPendingInputs] = useState(false);
    const [parameterError, setParameterError] = useState<string | null>(null);
    const [isSavingParameters, setIsSavingParameters] = useState(false);
    const [localRunStatus, setLocalRunStatus] = useState<LocalRunStatus>('idle');
    const [localRunError, setLocalRunError] = useState<string | null>(null);
    const [isRefreshingResults, setIsRefreshingResults] = useState(false);
    const [reportTab, setReportTab] = useState<RunReportTab | null>(null);
    const [lastReportTab, setLastReportTab] = useState<RunReportTab>('verification');
    const [shownOnGraph, setShownOnGraph] = useState(() => getDefaultShownOnGraph(rawData));
    const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
    // The simulation's history, newest first, to pick a run from.
    const [executions, setExecutions] = useState<ExecutionSummary[]>([]);
    // The graph shows a local WebSocket run, which has no execution to link to.
    const [isShowingLocalRun, setIsShowingLocalRun] = useState(false);
    const [isLinkCopied, setIsLinkCopied] = useState(false);

    // The execution on the graph: the one in the URL, or else the latest.
    const shownExecutionId = isShowingLocalRun ? null : executionId ?? executions[0]?.id ?? null;

    React.useEffect(() => {
        if (simulationId) {
            getExecutions(simulationId).then(setExecutions);
        }
    }, [simulationId]);

    const showExecution = (id: string | null) => {
        navigate(id ? `/results/${simulationId}/executions/${id}` : `/results/${simulationId}`, { state: { simulationName } });
    };

    const runOptions = useMemo(() => {
        const options = [
            { value: '', label: `Latest${executions[0] ? ` (#${executions[0].id})` : ''}` },
            ...executions.map((execution) => ({ value: execution.id, label: `#${execution.id} · ${execution.createdAt} UTC` })),
        ];

        // A run older than the fetched history still shows as picked.
        if (executionId && !executions.some((execution) => execution.id === executionId)) {
            options.push({ value: executionId, label: `#${executionId}` });
        }

        return options;
    }, [executionId, executions]);

    const copyLink = async () => {
        if (!shownExecutionId || !simulationId) {
            return;
        }

        try {
            await navigator.clipboard.writeText(`${window.location.origin}/results/${encodeURIComponent(simulationId)}/executions/${shownExecutionId}`);
            setIsLinkCopied(true);
            window.setTimeout(() => setIsLinkCopied(false), 2000);
        } catch (error) {
            console.error(error);
        }
    };

    React.useEffect(() => {
        storeViewState(simulationId, { graphMode, traceVisibility, selected3D, selectedPlayer, editingInput, transforms, autoRefresh });
    }, [autoRefresh, editingInput, graphMode, selected3D, selectedPlayer, simulationId, traceVisibility, transforms]);

    // The 2D graph shows the inputs as edited for the next run.
    const editedGraphData = useMemo<ApiGraphData>(() => ({ ...graphData, inputs: inputValues }), [graphData, inputValues]);

    const legendItems = useMemo(() => getLegendItems(editedGraphData), [editedGraphData]);
    const valueRanges = useMemo(() => new Map(
        Array.from(getSignalSeries(editedGraphData))
            .map(([name, series]) => [name, getValueRange(series)] as const)
            .filter((entry): entry is [string, ValueRange] => entry[1] !== null),
    ), [editedGraphData]);

    const setEditingInput = (label: string | null) => {
        setEditingInputState(label);

        if (label) {
            setAdjustingSignal(null);
        }
    };

    const toggleAdjustingSignal = (name: string) => {
        setAdjustingSignal((current) => current === name ? null : name);
        setEditingInputState(null);
    };

    const stopAdjusting = React.useCallback(() => setAdjustingSignal(null), []);

    const handleTransformChange = (name: string, transform: SignalTransform) => {
        setTransforms((current) => {
            const { [name]: _previous, ...others } = current;
            return isIdentityTransform(transform) ? others : { ...others, [name]: transform };
        });
    };
    const axisOptions = useMemo(() => getAxisOptions(graphData), [graphData]);
    const selection3D = useMemo(() => resolveSelection(selected3D, getDefaultAxes3D(axisOptions), axisOptions, AXES_3D), [selected3D, axisOptions]);
    const selectionPlayer = useMemo(() => resolveSelection(selectedPlayer, getDefaultPlayerAxes(axisOptions), axisOptions, PLAYER_AXES), [selectedPlayer, axisOptions]);
    const traces3D = useMemo(() => getAxisTraces(selection3D, AXES_3D), [selection3D]);
    const tracesPlayer = useMemo(() => getAxisTraces(selectionPlayer, PLAYER_AXES), [selectionPlayer]);

    const toggleTrace = (name: string) => {
        setTraceVisibility((prevVisibility) => ({
            ...prevVisibility,
            [name]: prevVisibility[name] === false,
        }));
    };

    // Show only the given signal, or show every signal again when it already
    // is the only one visible.
    const isolateTrace = (name: string) => {
        setTraceVisibility((prevVisibility) => {
            const isAlreadyIsolated = legendItems.every((item) => (
                (prevVisibility[item.name] !== false) === (item.name === name)
            ));

            if (isAlreadyIsolated) {
                return {};
            }

            return Object.fromEntries(legendItems.map((item) => [item.name, item.name === name]));
        });
    };

    // Reset the editors to what the next execution will read.
    const loadNextRunValues = React.useCallback((execution: ApiGraphData, pending: InputsDocument | null) => {
        const { parameterValues: nextParameterValues, inputs } = buildNextRunValues(execution, pending);

        setParameterValues(nextParameterValues);
        setOriginalParameterValues(nextParameterValues);
        setLastRunParameterValues(nextParameterValues);
        setInputValues(inputs);
        setOriginalInputValues(inputs);
        setLastRunInputValues(inputs);
        setHasPendingInputs(pending !== null);
        setParameterError(null);
    }, []);

    React.useEffect(() => {
        let isMounted = true;

        const loadInputs = async () => {
            const pending = simulationId ? await getSimulationInputs(simulationId) : null;

            if (isMounted) {
                loadNextRunValues(rawData, pending);
            }
        };

        loadInputs();

        return () => {
            isMounted = false;
        };
    }, [loadNextRunValues, rawData, simulationId]);

    const simulationParameters = useMemo<Parameter[]>(() => {
        return Object.entries(parameterValues)
            .filter(([name]) => !name.startsWith('CppModel.'))
            .map(([name, value]) => ({ name, value }));
    }, [parameterValues]);

    const testStatus = useMemo(() => getTestStatus(graphData.results), [graphData.results]);

    // Each newly loaded run starts with its own violations shown.
    React.useEffect(() => {
        setShownOnGraph(getDefaultShownOnGraph(graphData));
    }, [graphData]);

    const verificationSummary = useMemo(() => {
        const outcomes = Object.values(graphData.verification ?? {});
        return outcomes.length > 0
            ? { passed: outcomes.filter((outcome) => outcome.pass).length, total: outcomes.length }
            : null;
    }, [graphData.verification]);
    const requirementCount = Object.keys(graphData.requirements ?? {}).length;

    const graphHighlights = useMemo<GraphHighlight[]>(() => [
        ...Object.entries(graphData.verification ?? {})
            .filter(([name, outcome]) => outcome.violations.length > 0 && shownOnGraph.has(ruleHighlightId(name)))
            .map(([name, outcome]) => ({ id: ruleHighlightId(name), label: name, kind: 'violation' as const, intervals: outcome.violations })),
        ...Object.entries(graphData.requirements ?? {})
            .filter(([id]) => shownOnGraph.has(requirementHighlightId(id)))
            .flatMap(([id, coverage]) => {
                const interval = getCoverageInterval(coverage);
                return interval ? [{ id: requirementHighlightId(id), label: id, kind: 'coverage' as const, intervals: [interval] }] : [];
            }),
    ], [graphData.requirements, graphData.verification, shownOnGraph]);

    const openReport = (tab: RunReportTab) => {
        setReportTab(tab);
        setLastReportTab(tab);
    };

    const toggleShownOnGraph = (id: string) => {
        setShownOnGraph((current) => {
            const next = new Set(current);

            if (!next.delete(id)) {
                next.add(id);
            }

            return next;
        });
    };

    // Zooming needs the 2D graph, so switch to it.
    const focusInterval = (interval: [number | null, number | null]) => {
        setGraphMode('2d');
        setAdjustingSignal(null);
        setFocusRequest((current) => ({ range: getFocusRange(interval, getRunEnd(graphData)), nonce: (current?.nonce ?? 0) + 1 }));
    };

    const duration = parameterValues[DURATION_PARAMETER] ?? '';
    const stepSize = parameterValues[STEP_SIZE_PARAMETER] ?? '';

    const validationError = useMemo(() => {
        for (const [name, value] of Object.entries(parameterValues)) {
            if (value.trim().length === 0) {
                return `Parameter "${name}" cannot be empty.`;
            }

            if (Number.isNaN(Number(value))) {
                return `Parameter "${name}" must be a valid number.`;
            }
        }

        return null;
    }, [parameterValues]);

    const changedInputs = useMemo(() => new Set(
        inputValues
            .filter((input) => !areSeriesEqual(input, originalInputValues.find((original) => original.label === input.label)))
            .map((input) => input.label)
    ), [inputValues, originalInputValues]);

    const hasUnsavedChanges = useMemo(() => {
        return !areParameterValuesEqual(originalParameterValues, parameterValues) || changedInputs.size > 0;
    }, [changedInputs, originalParameterValues, parameterValues]);

    const hasLocalRunChanges = useMemo(() => {
        return !areParameterValuesEqual(lastRunParameterValues, parameterValues)
            || !areSeriesListsEqual(lastRunInputValues, inputValues);
    }, [inputValues, lastRunInputValues, lastRunParameterValues, parameterValues]);

    const handleParameterChange = (name: string, value: string) => {
        setParameterValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
        setParameterError(null);
        setLocalRunStatus('idle');
        setLocalRunError(null);
    };

    const handleInputChange = React.useCallback((series: InputSeries) => {
        setInputValues((prevInputs) => prevInputs.map((input) => (input.label === series.label ? series : input)));
        setParameterError(null);
        setLocalRunStatus('idle');
        setLocalRunError(null);
    }, []);

    const handleResetInput = (label: string) => {
        const original = originalInputValues.find((input) => input.label === label);

        if (original) {
            handleInputChange(original);
        }
    };

    const handleRunSimulation = async () => {
        if (validationError || localRunStatus === 'running') {
            return;
        }

        setLocalRunStatus('running');
        setLocalRunError(null);

        try {
            const request = buildLocalRunRequest(parameterValues, inputValues);
            const results = normalizeGraphData(await runLocalSimulation(request));

            setGraphData(results);
            setExecutionTime(results.executionTime);
            setIsShowingLocalRun(true);
            setLastRunParameterValues(parameterValues);
            setLastRunInputValues(inputValues);
            setLocalRunStatus('completed');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Local simulation failed.';

            setLocalRunError(message);
            setLocalRunStatus('failed');
        }
    };

    // Load the newest execution. It may have consumed the pending inputs, so
    // the editors follow it too - unless they hold unsaved edits.
    const handleRefreshResults = async () => {
        if (!simulationId || isRefreshingResults) {
            return;
        }

        setIsRefreshingResults(true);

        const [refreshedData, pending, refreshedExecutions] = await Promise.all([
            getSimulationResultsById(simulationId),
            getSimulationInputs(simulationId),
            getExecutions(simulationId),
        ]);

        setExecutions(refreshedExecutions);

        if (refreshedData) {
            const normalizedData = normalizeGraphData(refreshedData);
            setGraphData(normalizedData);
            setExecutionTime(normalizedData.executionTime);
            setIsShowingLocalRun(false);

            if (!hasUnsavedChanges) {
                loadNextRunValues(normalizedData, pending);
            }
        }

        setIsRefreshingResults(false);
    };

    // With auto-refresh on, load new executions as they are submitted: poll
    // the id of the newest one and refresh when it changes. Paused while the
    // tab is hidden. Switching it on also loads anything submitted meanwhile.
    // A linked execution never changes, so it is never refreshed.
    const refreshResultsRef = React.useRef(handleRefreshResults);
    refreshResultsRef.current = handleRefreshResults;

    React.useEffect(() => {
        if (!simulationId || !autoRefresh || executionId) {
            return;
        }

        let knownExecutionId: string | null | undefined;
        let isPolling = false;

        const poll = async () => {
            if (document.hidden || isPolling) {
                return;
            }

            isPolling = true;
            const latestExecutionId = await getLatestExecutionId(simulationId);
            isPolling = false;

            if (latestExecutionId === null) {
                return;
            }

            // Refresh on any new execution, and once on switching on in case
            // one arrived while auto-refresh was off.
            if (latestExecutionId !== knownExecutionId) {
                refreshResultsRef.current();
            }

            knownExecutionId = latestExecutionId;
        };

        poll();
        const interval = window.setInterval(poll, EXECUTION_POLL_INTERVAL);
        document.addEventListener('visibilitychange', poll);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', poll);
        };
    }, [autoRefresh, executionId, simulationId]);

    // Submit the parameters and inputs to the workspace as the document the
    // next execution consumes. A local run over the WebSocket is not stored in
    // the workspace, so this is how its values reach the next real execution.
    const handleSaveParameters = async () => {
        if (!simulationId || validationError) {
            return;
        }

        setIsSavingParameters(true);
        setParameterError(null);

        const saved = await saveSimulationInputs(simulationId, buildInputsDocument(parameterValues, inputValues));

        if (!saved) {
            setParameterError('Could not save the parameters and inputs. Please try again.');
            setIsSavingParameters(false);
            return;
        }

        setOriginalParameterValues(parameterValues);
        setOriginalInputValues(inputValues);
        setHasPendingInputs(true);
        setIsSavingParameters(false);
    };

    if (executionId && !loaderData) {
        return (
            <div className="cm-page">
                <div className="cm-page-header">
                    <h1 className="cm-page-title">Results for {simulationName}</h1>
                </div>
                <div className="cm-empty">
                    Run #{executionId} of this simulation was not found. It may have been deleted.
                    <div style={{ marginTop: '12px' }}>
                        <Button onClick={() => showExecution(null)}>Show the latest run</Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cm-results">
            <div className="cm-page-header" style={{ marginBottom: '16px' }}>
                <h1 className="cm-page-title">Results for {simulationName}{executionId ? ` · run #${executionId}` : ''}</h1>
                <div className="cm-page-header-actions">
                    <Tooltip content={isLinkCopied ? 'Link copied' : shownExecutionId ? `Copy a link to run #${shownExecutionId}` : 'A local run has no link'}>
                        <Button icon={isLinkCopied ? IconNames.Tick : IconNames.Link} onClick={copyLink} disabled={!shownExecutionId}>
                            Copy link
                        </Button>
                    </Tooltip>
                    <Tooltip content={executionId ? `Compare run #${executionId} with the one before it` : 'Compare the latest run with the one before it'}>
                        <Button
                            icon={IconNames.Changes}
                            onClick={() => navigate(`/what-changed/${simulationId}${executionId ? `?current=${executionId}` : ''}`, { state: { simulationName } })}
                        >
                            What changed
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {/* Graph and the resizable legend/parameters pane */}
            <div className={`cm-results-body${isResizingPane ? ' is-resizing' : ''}`} ref={resultsBodyRef}>

                <Card className="cm-graph-card">
                    <div className="cm-graph-toolbar">
                        <SegmentedControl
                            small
                            options={[{ label: '2D', value: '2d' }, { label: '3D', value: '3d' }, { label: 'Player', value: 'player' }, { label: 'Table', value: 'table' }]}
                            value={graphMode}
                            onValueChange={(value) => {
                                setGraphMode(value as GraphMode);
                                setAdjustingSignal(null);
                            }}
                        />
                        {graphMode === '2d' && adjustingSignal && (
                            <span className="cm-graph-hint is-adjusting">
                                <strong>Adjusting {adjustingSignal}:</strong> drag to shift, edges snap to other signals and markers (Alt moves freely, Shift locks the direction) · scroll to scale · scroll over an axis to zoom
                                <Button small intent="primary" onClick={stopAdjusting}>Done</Button>
                            </span>
                        )}
                        {graphMode === '2d' && !adjustingSignal && (
                            <span className="cm-graph-hint">
                                {editingInput
                                    ? 'Scroll to zoom'
                                    : 'Click the graph to place markers A and B, drag to move them (they snap to signal edges, Alt for free) · Scroll to zoom'}
                            </span>
                        )}
                    </div>
                    <div className="cm-graph-area">
                        {/* <HeatMapCard data={rawData} /> */}
                        {graphMode === '3d' && <SimulationGraph3D data={graphData} selection={selection3D} traces={traces3D} />}
                        {graphMode === 'player' && <SimulationPlayer data={graphData} selection={selectionPlayer} traces={tracesPlayer} />}
                        {graphMode === 'table' && (
                            <SimulationTable
                                data={editedGraphData}
                                traceVisibility={traceVisibility}
                                fileName={`${simulationName ?? simulationId ?? 'simulation'}-results.csv`}
                            />
                        )}
                        {graphMode === '2d' && (
                            <SimulationGraph
                                data={editedGraphData}
                                traceVisibility={traceVisibility}
                                editingInput={editingInput}
                                onInputChange={handleInputChange}
                                referenceInputs={graphData.inputs}
                                transforms={transforms}
                                adjustingSignal={adjustingSignal}
                                onTransformChange={handleTransformChange}
                                onAdjustEnd={stopAdjusting}
                                highlights={graphHighlights}
                                focusRequest={focusRequest}
                            />
                        )}
                        {/* <GraphCard title="Simulation Results" data={plotData} /> */}
                    </div>
                </Card>

                <div className="cm-results-splitter" aria-label="Resize parameters pane" {...splitterProps} />

                <ParametersCard
                    width={paneWidth}
                    simulationParameters={simulationParameters}
                    onParameterChange={handleParameterChange}
                    legendItems={legendItems}
                    traceVisibility={traceVisibility}
                    onToggleTrace={toggleTrace}
                    onIsolateTrace={isolateTrace}
                    // Shifting and scaling only applies to the 2D graph.
                    {...(graphMode === '2d' ? {
                        transforms,
                        onTransformChange: handleTransformChange,
                        transformRanges: valueRanges,
                        editingTraceName: editingInput ? `In: ${editingInput}` : null,
                        adjustingSignal,
                        onAdjustToggle: toggleAdjustingSignal,
                    } : {})}
                    axisSelector={graphMode === '3d' ? (
                        <AxisSelector
                            axes={AXES_3D}
                            options={axisOptions}
                            selection={selection3D}
                            onChange={setSelected3D}
                            traces={traces3D}
                        />
                    ) : graphMode === 'player' ? (
                        <AxisSelector
                            axes={PLAYER_AXES}
                            options={axisOptions}
                            selection={selectionPlayer}
                            onChange={setSelectedPlayer}
                            traces={tracesPlayer}
                        />
                    ) : undefined}
                    // The player replays a run; editing happens in the other views.
                    hideEditors={graphMode === 'player'}
                    inputEditor={(
                        <InputEditor
                            inputs={inputValues}
                            editingLabel={editingInput}
                            onEditingLabelChange={setEditingInput}
                            onInputChange={handleInputChange}
                            changedLabels={changedInputs}
                            onResetInput={handleResetInput}
                        />
                    )}
                />
            </div>

            {/* Sticky Footer with Simulation Controls */}
            <div className="cm-results-footer">
                <label className="cm-footer-field">Run
                    <HTMLSelect
                        value={executionId ?? ''}
                        options={runOptions}
                        onChange={(event) => showExecution(event.currentTarget.value || null)}
                    />
                </label>
                <span className="cm-footer-meta">{isShowingLocalRun ? 'Local run at' : 'Run at'}: {executionTime || 'unknown'}</span>
                {testStatus && (
                    <Tooltip
                        content={testStatus.passed
                            ? `All ${testStatus.totalSteps} step results passed`
                            : `${testStatus.failedSteps} of ${testStatus.totalSteps} step results failed${testStatus.firstFailureTime !== null ? `, first at t = ${msToSeconds(testStatus.firstFailureTime)} s` : ''}`}
                    >
                        <span className={`cm-footer-status${testStatus.passed ? '' : ' is-failed'}`}>
                            <Icon icon={testStatus.passed ? IconNames.TickCircle : IconNames.Error} />
                            {testStatus.passed ? 'Test passed' : 'Test failed'}
                        </span>
                    </Tooltip>
                )}
                {verificationSummary && (
                    <Tooltip content="Show the outcome of each verification rule">
                        <Button
                            small
                            minimal
                            className={`cm-footer-report${verificationSummary.passed === verificationSummary.total ? '' : ' is-failed'}`}
                            icon={verificationSummary.passed === verificationSummary.total ? IconNames.Endorsed : IconNames.Error}
                            onClick={() => openReport('verification')}
                        >
                            Verification {verificationSummary.passed}/{verificationSummary.total}
                        </Button>
                    </Tooltip>
                )}
                {requirementCount > 0 && (
                    <Tooltip content="Show the requirements this run addresses">
                        <Button
                            small
                            minimal
                            className="cm-footer-report"
                            icon={IconNames.Document}
                            onClick={() => openReport('requirements')}
                        >
                            Requirements {requirementCount}
                        </Button>
                    </Tooltip>
                )}
                <span className="cm-footer-meta">Local run: {localRunStatus}</span>
                <Tooltip content={hasPendingInputs
                    ? 'Your next execution of this simulation uses the saved parameters and inputs'
                    : 'Nothing is saved for your next execution: it uses the values in the simulation code'}
                >
                    <span className="cm-footer-meta">Next run: {hasPendingInputs ? 'saved values' : 'code defaults'}</span>
                </Tooltip>
                <label className="cm-footer-field">Simulation Duration
                    <InputGroup
                        placeholder="Duration"
                        value={duration}
                        onChange={(e) => handleParameterChange(DURATION_PARAMETER, e.target.value)}
                        rightElement={<span className="cm-input-unit">s</span>}
                        style={{ width: '80px' }}
                    />
                </label>
                <label className="cm-footer-field">Execution Step Size
                    <InputGroup
                        placeholder="Step Size"
                        value={stepSize}
                        onChange={(e) => handleParameterChange(STEP_SIZE_PARAMETER, e.target.value)}
                        rightElement={<span className="cm-input-unit">s</span>}
                        style={{ width: '80px' }}
                    />
                </label>
                <div className="cm-footer-actions">
                    {/* A linked run never changes; pick Latest to follow new runs. */}
                    {!executionId && (
                        <>
                            <Tooltip content="Load new executions automatically as they are submitted">
                                <Switch
                                    className="cm-footer-switch"
                                    label="Auto-refresh"
                                    checked={autoRefresh}
                                    onChange={(event) => setAutoRefresh(event.currentTarget.checked)}
                                />
                            </Tooltip>
                            <Tooltip content="Refresh the simulation results">
                                <Button
                                    onClick={handleRefreshResults}
                                    icon={IconNames.Refresh}
                                    loading={isRefreshingResults}
                                >
                                    Refresh
                                </Button>
                            </Tooltip>
                        </>
                    )}
                    <Tooltip content={validationError ?? "Save the parameters and inputs for your next execution"}>
                        <Button
                            onClick={handleSaveParameters}
                            icon={IconNames.TableSync}
                            loading={isSavingParameters}
                            disabled={!hasUnsavedChanges || Boolean(validationError) || isSavingParameters}
                        >
                            Save Parameters
                        </Button>
                    </Tooltip>
                    <Tooltip content={validationError ?? (hasLocalRunChanges ? `Run simulation through ${LOCAL_SIMULATION_WS_URL}` : 'Change a parameter or input to run the local simulation')}>
                        <Button
                            intent="primary"
                            onClick={handleRunSimulation}
                            icon={IconNames.Play}
                            loading={localRunStatus === 'running'}
                            disabled={!hasLocalRunChanges || Boolean(validationError) || localRunStatus === 'running'}
                        >
                            Run
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <RunReportDrawer
                isOpen={reportTab !== null}
                tab={reportTab ?? lastReportTab}
                onTabChange={openReport}
                onClose={() => setReportTab(null)}
                verification={graphData.verification}
                requirements={graphData.requirements}
                shownOnGraph={shownOnGraph}
                onToggleShown={toggleShownOnGraph}
                onFocus={focusInterval}
            />
            {parameterError && (
                <div className="cm-floating-error" style={{ bottom: '84px' }}>
                    {parameterError}
                </div>
            )}
            {localRunError && (
                <div className="cm-floating-error" style={{ bottom: parameterError ? '132px' : '84px' }}>
                    {localRunError}
                </div>
            )}
        </div>
    );
};

// The route keeps this element mounted when moving to another simulation or
// run, so key the page by both to start from that run's data and values.
const SimulationResults: React.FC = () => {
    const { simulationId, executionId } = useParams();

    return <SimulationResultsPage key={`${simulationId}:${executionId ?? ''}`} />;
};

export default SimulationResults;
